/**
 * ITER310 · Voucher QR Scanner — manager redemption terminal.
 *
 * Uses `html5-qrcode` (BSD-2, ~40kB gzipped) to open the device camera
 * inside the browser — works on any staff phone, no app install.
 *
 * Contract:
 *   • On scan → invoke `onScan(code)` with the detected string.
 *   • Caller runs the existing validation endpoint. We NEVER auto-
 *     redeem — bill-value capture at redemption powers loyalty
 *     economics dashboards, so a scan that skips it would silently
 *     break analytics.
 *   • Camera denied / no camera → show clear "manual entry" fallback
 *     copy; caller's manual input stays visible.
 *
 * We support two QR payload shapes:
 *   a) Raw voucher code (e.g. "AB12CD34")
 *   b) Loyalty URLs whose last segment or `?code=` query is the code
 *      (defensive parser — voucher-card SVGs today encode the code
 *       either way).
 */
import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../ui/button';
import { Camera, X, AlertTriangle } from 'lucide-react';

function extractCode(payload) {
  const raw = String(payload || '').trim();
  if (!raw) return '';
  // Case 1 — bare alphanumeric code (typical voucher SVG QR).
  if (/^[A-Za-z0-9\-_]{4,32}$/.test(raw)) return raw.toUpperCase();
  // Case 2 — URL: try `?code=` then last path segment.
  try {
    const url = new URL(raw);
    const q = url.searchParams.get('code');
    if (q) return q.toUpperCase();
    const last = url.pathname.split('/').filter(Boolean).pop();
    if (last && /^[A-Za-z0-9\-_]{4,32}$/.test(last)) return last.toUpperCase();
  } catch { /* not a URL */ }
  return raw.toUpperCase();
}

export default function VoucherQrScanner({ open, onClose, onScan }) {
  const containerId = 'voucher-qr-reader';
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    const scanner = new Html5Qrcode(containerId, { verbose: false });
    scannerRef.current = scanner;

    const start = async () => {
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (cancelled) return;
            const code = extractCode(decodedText);
            if (!code) return;
            // Stop before invoking so the camera closes immediately;
            // the caller unmounts us on close.
            scanner.stop().catch(() => {});
            onScan(code);
          },
          () => { /* per-frame no-match — quiet */ },
        );
      } catch (err) {
        if (cancelled) return;
        // Common: NotAllowedError (denied), NotFoundError (no camera).
        const name = err?.name || '';
        if (name === 'NotAllowedError') {
          setError('Camera permission denied. Enter the voucher code manually below.');
        } else if (name === 'NotFoundError') {
          setError('No camera detected on this device. Use manual entry.');
        } else {
          setError('Camera unavailable. Use manual entry.');
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop().catch(() => {}).finally(() => {
          try { s.clear(); } catch { /* ignore */ }
        });
      }
    };
  }, [open, onScan]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="qr-scanner-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
            <span className="text-sm font-semibold">Scan voucher QR</span>
          </div>
          <button onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-gray-100"
                  data-testid="qr-scanner-close"
                  aria-label="Close scanner">
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="relative">
          <div id={containerId} className="w-full aspect-square bg-black" />
          {error && (
            <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-6"
                 data-testid="qr-scanner-error">
              <div className="text-center max-w-xs">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <div className="text-sm text-gray-800 leading-relaxed">{error}</div>
                <Button onClick={onClose} className="mt-4 rounded-full"
                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                        data-testid="qr-scanner-fallback-btn">
                  Manual entry
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 text-[11px] text-gray-500 leading-relaxed bg-gray-50 border-t border-gray-100">
          Point the camera at the guest's voucher QR. Bill value is captured on the next step —
          scan does not auto-redeem.
        </div>
      </div>
    </div>
  );
}
